// src/manager/pages/ResetPassword.tsx
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import DOMPurify from 'dompurify';
import { Eye, EyeOff } from 'lucide-react';
import { useSearchParams, Link } from 'react-router-dom';
import { useManager } from '../hooks/useManager';
import PasswordStrength from '../../shared/components/PasswordStrength';
import ErrorDisplay from '../../shared/components/ErrorDisplay';

interface ResetPasswordForm {
  password: string;
}

export default function ManagerResetPassword() {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { fetchCsrfToken, error, setError } = useManager();
  const { register, handleSubmit, watch, formState: { errors } } = useForm<ResetPasswordForm>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const onSubmit = async (data: ResetPasswordForm) => {
    if (!token) {
      setError('Invalid or missing reset token');
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const csrfToken = await fetchCsrfToken();
      if (!csrfToken) throw new Error('No CSRF token');
      const res = await fetch('/manager/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        credentials: 'include',
        body: JSON.stringify({
          token: DOMPurify.sanitize(token),
          newPassword: data.password,
        }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Failed to reset password');
      setSuccess('Password reset successfully. Please log in.');
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-800 to-pink-700 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">Manager Reset Password</h2>

        {error && <ErrorDisplay error={error} onDismiss={() => setError('')} />}

        {success && (
          <div className="mb-4 p-4 bg-green-100 text-green-700 rounded-lg text-center">
            {success}
            <div className="mt-2">
              <Link to="/manager/login" className="text-purple-600 hover:underline">
                Back to Login
              </Link>
            </div>
          </div>
        )}

        {!success && (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <input type="hidden" name="_csrf" />

            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="password">
                New Password
              </label>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter new password"
                {...register('password', {
                  required: 'Password required',
                  minLength: { value: 8, message: 'Password must be at least 8 characters' },
                })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg pr-12 focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                aria-invalid={!!errors.password}
              />
              <button
                type="button"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-9 text-gray-600 transform -translate-y-1/2"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>

              <PasswordStrength password={watch('password') || ''} />

              {errors.password && <p className="text-red-600 text-sm">{errors.password.message}</p>}
            </div>

            <button
              type="submit"
              disabled={loading || !token}
              className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 transition transform hover:scale-105 disabled:opacity-70"
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        )}

        {!success && (
          <p className="mt-4 text-center text-gray-600 text-sm">
            <Link to="/manager/login" className="text-purple-600 hover:underline">
              Back to Login
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}